const Skeleton = ({
  variant,
  count = 1,
}: {
  variant:
    | "BookCardCompact"
    | "BookCardGrid"
    | "HomeRow"
    | "HomeWeb"
    | "BookDetail"
    | "CategoryGrid"
    | "Text"
    | "SearchResult";
  count?: number;
}) => {
  const items = Array.from({ length: count });

  if (variant === "BookCardCompact") {
    return (
      <>
        {items.map((_, i) => (
          <div key={i} className="shrink-0 w-28 flex flex-col items-start">
            <div className="skeleton w-full aspect-[2/3] rounded-xl" />
            <div className="skeleton h-3 w-[80%] mt-2 rounded" />
            <div className="skeleton h-2.5 w-[60%] mt-1 rounded" />
          </div>
        ))}
      </>
    );
  }

  if (variant === "BookCardGrid") {
    return (
      <>
        {items.map((_, i) => (
          <div key={i} className="w-full flex flex-col items-start">
            <div className="skeleton w-full aspect-[2/3] rounded-xl" />
            <div className="skeleton h-3.5 w-[85%] mt-2 rounded" />
            <div className="skeleton h-3 w-[60%] mt-1 rounded" />
            <div className="skeleton h-5 w-16 mt-1.5 rounded-full" />
          </div>
        ))}
      </>
    );
  }

  if (variant === "HomeRow") {
    return (
      <>
        {/* Masonry cover skeleton */}
        <div className="px-2.5 columns-2 gap-2.5 mb-3">
          {["h-56", "h-64", "h-72", "h-52", "h-60", "h-48"].map((h, i) => (
            <div key={i} className="mb-2.5 break-inside-avoid">
              <div className={`skeleton ${h} rounded-2xl w-full`} />
            </div>
          ))}
        </div>
        {/* Horizontal row skeletons */}
        {items.map((_, i) => (
          <section key={i} className="mt-5">
            <div className="px-5 mb-3">
              <div className="skeleton h-5 w-32 rounded" />
            </div>
            <div className="flex gap-3 overflow-hidden px-5">
              <Skeleton variant="BookCardCompact" count={4} />
            </div>
          </section>
        ))}
      </>
    );
  }

  if (variant === "HomeWeb") {
    return (
      <div className="w-full h-full relative bg-gradient-to-br from-neutral-800 to-neutral-950">
        {/* Right-side action buttons (mobile only) */}
        <div className="absolute right-4 bottom-[25%] flex flex-col items-center gap-6 md:hidden">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton w-14 h-14 !rounded-full !bg-white/10" />
          ))}
        </div>
        {/* Bottom gradient with description lines */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-24 pb-[calc(env(safe-area-inset-bottom,24px)+24px)] px-6">
          <div className="pr-20 md:pr-0">
            <div className="skeleton h-4 w-full mb-2 rounded !bg-white/15" />
            <div className="skeleton h-4 w-full mb-2 rounded !bg-white/15" />
            <div className="skeleton h-4 w-3/4 mb-3 rounded !bg-white/15" />
            <div className="skeleton h-3 w-24 rounded !bg-white/20" />
          </div>
        </div>
      </div>
    );
  }

  if (variant === "BookDetail") {
    return (
      <>
        {/* Mobile skeleton — compact hero */}
        <div className="md:hidden">
          {/* Centered cover thumbnail */}
          <div className="flex justify-center px-5 mb-4 pt-2">
            <div className="skeleton w-[160px] aspect-[2/3] rounded-2xl" />
          </div>
          <div className="flex flex-col items-center px-5">
            <div className="skeleton h-5 w-56 mb-3 rounded" />
            <div className="skeleton h-3 w-40 mb-4 rounded" />
            <div className="skeleton h-4 w-full mb-1.5 rounded" />
            <div className="skeleton h-4 w-full mb-1.5 rounded" />
            <div className="skeleton h-4 w-full mb-1.5 rounded" />
            <div className="skeleton h-4 w-3/4 mb-6 rounded" />
            <div className="skeleton h-12 w-full rounded-full" />
          </div>
        </div>
        {/* Desktop skeleton — side-by-side hero */}
        <div className="hidden md:block">
          <div className="flex gap-8 mb-6">
            <div className="skeleton shrink-0 w-56 aspect-[2/3] rounded-2xl" />
            <div className="flex-1 flex flex-col justify-center gap-3">
              <div className="skeleton h-7 w-72 rounded" />
              <div className="flex items-center gap-3">
                <div className="skeleton h-7 w-16 rounded-full" />
                <div className="skeleton h-7 w-24 rounded-full" />
                <div className="skeleton h-7 w-20 rounded-full" />
              </div>
              <div className="flex flex-wrap gap-1.5">
                <div className="skeleton h-6 w-16 rounded-full" />
                <div className="skeleton h-6 w-20 rounded-full" />
                <div className="skeleton h-6 w-14 rounded-full" />
              </div>
              <div className="skeleton h-12 w-40 rounded-full mt-2" />
            </div>
          </div>
          <div className="skeleton h-4 w-full mb-1.5 rounded" />
          <div className="skeleton h-4 w-full mb-1.5 rounded" />
          <div className="skeleton h-4 w-3/4 mb-6 rounded" />
        </div>
      </>
    );
  }

  if (variant === "CategoryGrid") {
    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="skeleton w-full aspect-[2/3] rounded-xl" />
        ))}
      </div>
    );
  }

  if (variant === "Text") {
    const widths = [
      "100%", "95%", "88%", "100%", "72%", "96%",
      "100%", "80%", "93%", "100%", "68%", "90%",
    ];
    return (
      <div className="px-5 py-8 max-w-[680px] mx-auto space-y-4">
        {widths.map((w, i) => (
          <div
            key={i}
            className="skeleton h-4 rounded"
            style={{ width: w }}
          />
        ))}
      </div>
    );
  }

  if (variant === "SearchResult") {
    return (
      <div className="px-2 pb-2 flex flex-col gap-1">
        {items.map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-5 py-4">
            <div className="skeleton shrink-0 w-12 h-16 rounded-lg" />
            <div className="flex-1 min-w-0">
              <div className="skeleton h-4 w-[70%] mb-2 rounded" />
              <div className="skeleton h-3 w-[50%] rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export default Skeleton;
